'use client'

import {useRef, useState} from "react";
import ListingPhoto from "@/components/listing-photo";
import {ComingSoonPhoto} from "@/components/listings/coming-soon";
import type {UsablePhoto} from "@/lib/listing-photos";
import ClassicZoom from "./classic-zoom";

/**
 * Photo grid with a zoom dialog. Receives already-validated photos as props; with none it shows
 * the "coming soon" tile. Every photo is shown whole, in the grid and the zoom, on grey (REALTOR.ca
 * listing content is never cropped here). The zoom's previous/next buttons sit below the photo,
 * never over it.
 */
export default function ClassicGallery({photos, address}: {
    photos: UsablePhoto[];
    address: string;
}) {
    const [zoomIndex, setZoomIndex] = useState<number | null>(null);
    // The thumbnails and the photo shown last, so focus returns to that one on close.
    const thumbs = useRef<(HTMLButtonElement | null)[]>([]);
    const lastShown = useRef(0);
    // No photo: one "coming soon" tile. It isn't a button, opens no zoom and isn't counted as a photo.
    if (photos.length === 0) {
        return (
            <div className="relative mx-auto aspect-[4/3] w-full max-w-md rounded bg-zinc-200">
                <ComingSoonPhoto sizes="(min-width: 768px) 448px, 100vw"/>
            </div>
        );
    }
    const alt = address ? `Photo of ${address}` : "Listing photo";

    const show = (i: number) => {
        lastShown.current = i;
        setZoomIndex(i);
    };

    return (
        <>
            <ClassicZoom
                photos={photos}
                index={zoomIndex}
                address={address}
                onIndexChange={show}
                onClose={() => setZoomIndex(null)}
                onCloseAutoFocus={(event) => {
                    event.preventDefault();
                    const thumb = thumbs.current[lastShown.current];
                    thumb?.focus({preventScroll: true});
                    thumb?.scrollIntoView({block: "nearest"});
                }}
            />
            <div className="grid grid-flow-row grid-cols-2 md:grid-cols-5 gap-3">
                {photos.map((photo, i) => (
                    <button key={photo.src} type="button"
                            ref={(el) => { thumbs.current[i] = el; }}
                            onClick={() => show(i)}
                            aria-label={`Enlarge photo ${i + 1} of ${photos.length}`}
                            className="relative aspect-[4/3] cursor-pointer rounded shadow-2xl block overflow-hidden bg-zinc-200">
                        <ListingPhoto
                            src={photo.src}
                            alt={`${alt} ${i + 1}`}
                            sizes="(min-width: 768px) 20vw, 50vw"
                            fit="contain"
                            priority={i === 0}
                        />
                    </button>
                ))}
            </div>
        </>
    );
}
